import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '@/lib/operator-api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { NativeFlowPreview } from '@/components/flow-builder/NativeFlowPreview';
import { Upload, Send, FileJson, CheckCircle2, AlertCircle, Clock, BarChart3 } from 'lucide-react';
import type { FlowTabProps, FlowSummary } from './types';

// -- Types ------------------------------------------------------------------

interface ActionResult {
  success: boolean;
  message?: string;
  error?: string;
  flowJson?: unknown;
  metaFlowId?: string;
}

// -- API helpers -------------------------------------------------------------

async function generateJson(flowSlug: string, businessId: string): Promise<ActionResult> {
  const res = await apiFetch(
    `/api/operator/flows/${encodeURIComponent(flowSlug)}/generate-json?businessId=${encodeURIComponent(businessId)}`,
    { method: 'POST' },
  );
  return res.json();
}

async function publishFlow(flowSlug: string, businessId: string): Promise<ActionResult> {
  const res = await apiFetch(
    `/api/operator/flows/${encodeURIComponent(flowSlug)}/publish?businessId=${encodeURIComponent(businessId)}`,
    { method: 'POST' },
  );
  return res.json();
}

async function sendTest(flowSlug: string, businessId: string, phoneNumber: string): Promise<ActionResult> {
  const res = await apiFetch(
    `/api/operator/flows/${encodeURIComponent(flowSlug)}/send-test?businessId=${encodeURIComponent(businessId)}`,
    { method: 'POST', body: JSON.stringify({ phoneNumber }) },
  );
  return res.json();
}

// -- Status badge ------------------------------------------------------------

function FlowStatusBadge({ flow }: { flow: FlowSummary }) {
  const { t } = useTranslation();
  if (flow.metaFlowId && !flow.metaFlowId.startsWith('pending-')) {
    return (
      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 gap-1">
        <CheckCircle2 className="h-3 w-3" />
        {t('nativeFlows.published')}
      </Badge>
    );
  }
  if (flow.metaFlowId?.startsWith('pending-')) {
    return (
      <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 gap-1">
        <Clock className="h-3 w-3" />
        {t('nativeFlows.pending')}
      </Badge>
    );
  }
  if (flow.flowJson) {
    return (
      <Badge variant="secondary" className="gap-1">
        <FileJson className="h-3 w-3" />
        {t('nativeFlows.jsonGenerated')}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1">
      <AlertCircle className="h-3 w-3" />
      {t('nativeFlows.draft')}
    </Badge>
  );
}

// -- FlowPublishTab ----------------------------------------------------------

export function FlowPublishTab({ flow, flowSlug, businessId, onFlowUpdated }: FlowTabProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<string | null>(null);
  const [result, setResult] = useState<ActionResult | null>(null);
  const [testPhone, setTestPhone] = useState('');
  const [previewJson, setPreviewJson] = useState<unknown>(flow.flowJson || null);

  const handleAction = useCallback(async (action: string) => {
    setLoading(action);
    setResult(null);
    try {
      let res: ActionResult;
      if (action === 'generate') {
        res = await generateJson(flowSlug, businessId);
        if (res.success && res.flowJson) setPreviewJson(res.flowJson);
      } else if (action === 'publish') {
        res = await publishFlow(flowSlug, businessId);
      } else {
        if (!testPhone.trim()) {
          setResult({ success: false, error: t('nativeFlows.enterPhone') });
          setLoading(null);
          return;
        }
        res = await sendTest(flowSlug, businessId, testPhone.trim());
      }
      setResult(res);
      if (res.success && (action === 'generate' || action === 'publish')) {
        onFlowUpdated();
      }
    } catch (err) {
      setResult({
        success: false,
        error: err instanceof Error ? err.message : t('nativeFlows.unknownError'),
      });
    } finally {
      setLoading(null);
    }
  }, [flowSlug, businessId, testPhone, onFlowUpdated, t]);

  return (
    <div className="space-y-6">
      {/* Status badge + Meta Flow ID */}
      <div className="flex items-center gap-3">
        <FlowStatusBadge flow={flow} />
        {flow.metaFlowId && (
          <span className="text-xs text-muted-foreground font-mono">
            {t('nativeFlows.metaFlowId')} {flow.metaFlowId}
          </span>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" onClick={() => handleAction('generate')} disabled={loading !== null}>
          <FileJson className="h-4 w-4 mr-1.5" />
          {loading === 'generate' ? t('nativeFlows.generating') : t('nativeFlows.generateJson')}
        </Button>
        <Button variant="outline" onClick={() => handleAction('publish')} disabled={loading !== null}>
          <Upload className="h-4 w-4 mr-1.5" />
          {loading === 'publish' ? t('nativeFlows.publishing') : t('nativeFlows.publishWhatsApp')}
        </Button>
      </div>

      <Separator />

      {/* Send test */}
      <div className="space-y-3">
        <Label>{t('nativeFlows.sendTestLabel')}</Label>
        <div className="flex gap-2">
          <Input
            placeholder="5511999998888"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            className="max-w-[200px] font-mono text-sm"
          />
          <Button onClick={() => handleAction('test')} disabled={loading !== null || !testPhone.trim()}>
            <Send className="h-4 w-4 mr-1.5" />
            {loading === 'test' ? t('nativeFlows.sending') : t('nativeFlows.sendTest')}
          </Button>
        </div>
      </div>

      {/* Result banner */}
      {result && (
        <div
          className={`rounded-md px-4 py-3 text-sm ${
            result.success
              ? 'bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
              : 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
          }`}
        >
          {result.message || result.error}
        </div>
      )}

      {/* JSON preview */}
      {previewJson ? (
        <>
          <Separator />
          <NativeFlowPreview flowJson={previewJson as any} />
        </>
      ) : null}

      {/* Metrics placeholder */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">{t('nativeFlows.metrics')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('nativeFlows.metricsDesc')}</p>
        </CardContent>
      </Card>
    </div>
  );
}
